import { Application, ObservablePoint, Point, PointData } from 'pixi.js';
import { Util } from '../util/util';
import { Mouse } from './mouse';
import { Gfx } from './gfx';
import { Settings } from './settings';
import { Colors } from './colors';
import { Store } from 'src/sync/store';
import { Account } from 'src/sync/account';
import { Room } from 'src/sync/room';
import { Viewport } from 'lib/viewport';
import { L } from 'src/sync/_api';

export class View {
    public static app: Application;
    public static viewport: Viewport;
    public static timer: any;
    public static gridQualityMod: number;
    public static zoomDisabled = false;

    public static async initialize() {
        /* Create the view */
        const settings = Account.settings.get();
        View.app = new Application();
        await View.app.init({
            antialias: settings.highquality,
            resolution: Account.getResolution(),
            backgroundColor: Colors.appBackground,
            autoDensity: true,
            resizeTo: window,
        });
        View.viewport = View.buildViewport();
        View.viewport.visible = false; // Hide until loaded
        View.gridQualityMod = settings.highquality ? 0.5 : 1.5;

        /* Initialize the view and related architecture */
        await Gfx.initialize();
        View.app.stage.eventMode = 'static';
        View.viewport.options.disableOnContextMenu = true;
        View.viewport.wheel({ smooth: 3, percent: 0.5 });
        View.viewport.pinch();
        View.viewport.clampZoom({ minScale: Settings.minZoomScale, maxScale: Settings.maxZoomScale });
        View.viewport.on('zoomed', () => {
            View.redraw();
            View.save();
        });
        window.addEventListener('resize', () => View.viewport.resize());
        View.viewport.resize();
        Gfx.playArea.clear()
            .rect(0, 0, View.viewport.worldWidth, View.viewport.worldHeight)
            .fill({ color: Colors.viewBackground })
            .stroke({ width: Settings.viewBorderThickness, color: Colors.viewBorder, alignment: 0 });

        Mouse.addEvents();

        /* Set view scale/position */
        const attendee = Room.attendance.get(Account.id);
        if (attendee.view == null) {
            /* Delay until an active layer has been set */
            setTimeout(() => View.fitExtents());
        } else {
            View.viewport.setZoom(attendee.view.scale);
            View.viewport.moveCenter(attendee.view.position.x, attendee.view.position.y);
            View.redraw();
        }
        View.viewport.visible = true; // Show only if everything loads

        Room.awareness.on('change', () => {
            const syncRequest = Room.awareness.getStateFields<{ scale: number, position: { x: number, y: number } }>('sync-view').filter(x => x != null);
            if (syncRequest.length > 0) {
                View.viewport.setZoom(syncRequest[0].scale);
                View.viewport.moveCenter(syncRequest[0].position.x, syncRequest[0].position.y);
                View.redraw();
            }
        });
    }

    public static apply(center: { x: number, y: number }, scale: number | ObservablePoint) {
        View.viewport.scale = scale;
        View.viewport.center = center as Point;
    }

    public static redraw() {
        Mouse.draw();
        Store.activeSprite.value?.redraw();
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
            View.draw();
        }, 7);
    }

    public static coerce(value: number, snapAdjust: boolean = false): number {
        const halfway = Settings.worldSize / Settings.gridOffset / 2;
        let coerced = Math.floor((value / Settings.gridOffset) - halfway);

        if (snapAdjust) {
            if (coerced >= 0) coerced++;
        }
        return Math.max(-halfway, Math.min(coerced, halfway));
    }

    public static fitExtents() {
        const extents = Gfx.activeLayer === Gfx.layers[L.Mapping]
            ? Gfx.layers[L.Mapping].getExtents()
            : Gfx.layers[L.Fog].getExtents();

        if (extents[0] == Infinity) {
            View.viewport.moveCenter(View.viewport.worldWidth / 2, View.viewport.worldHeight / 2);
            View.viewport.fit(true, 2000, 2000);
        } else {
            const midpointFunc = ([x1, y1, x2, y2]: number[]) => [(x1 + x2) / 2, (y1 + y2) / 2];
            const midpoint = midpointFunc(extents);

            const windowWidth = parseInt((View.app.canvas.style?.width as any));
            const windowHeight = parseInt((View.app.canvas.style?.height as any));
            const ratioWidth = windowWidth / (windowWidth - 400)
            const ratioHeight = windowHeight / (windowHeight - 90)
            const width = (extents[2] - extents[0]) * ratioWidth;
            const height = (extents[3] - extents[1]) * ratioHeight;
            View.viewport.fit(true, width * 1.1, height * 1.1);

            const xOffset = (1 / View.viewport.scale.x) * 100;
            const yOffset = (1 / View.viewport.scale.y) * 20;
            View.viewport.moveCenter(midpoint[0] + xOffset, midpoint[1] - yOffset);
        }
        View.save();
        View.redraw();
    }

    public static throttleSave: any;
    public static save() {
        clearTimeout(View.throttleSave)
        View.throttleSave = setTimeout(() => {
            const scale = View.viewport.scale.x;
            const position = { x: View.viewport.center.x, y: View.viewport.center.y };
            Room.attendance.modify(Account.id, x => x.view = { scale, position });
        }, 500);
    }

    /* Integers are used to reduce the size of stored points */
    public static bindToWorld(point: PointData, xAdjust = 0, yAdjust = 0): PointData {
        const x = Math.round(Math.min(Math.max(0 - xAdjust, point.x), Settings.worldSize - xAdjust));
        const y = Math.round(Math.min(Math.max(0 - yAdjust, point.y), Settings.worldSize - yAdjust));
        return { x, y };
    }

    private static draw() {
        let gridThickness = Math.max(1, (1 / View.viewport.scale.x) * View.gridQualityMod);
        Gfx.playGrid.clear();
        for (var x = 0; x <= View.viewport.worldWidth; x += Settings.gridOffset) {
            Gfx.playGrid.moveTo(x, 0);
            Gfx.playGrid.lineTo(x, View.viewport.worldHeight);
        }

        for (var x = 0; x <= View.viewport.worldHeight; x += Settings.gridOffset) {
            Gfx.playGrid.moveTo(0, x);
            Gfx.playGrid.lineTo(View.viewport.worldWidth, x);
        }
        Gfx.playGrid.stroke({ width: gridThickness, color: Colors.grid, alpha: 0.1 });
        Gfx.playGrid.interactive = false;
        Gfx.playGrid.eventMode = 'none';
    }

    private static buildViewport() {
        return Util.suppressWarnings(() => {
            return View.app.stage.addChild(new Viewport({
                screenWidth: View.app.renderer.width,
                screenHeight: View.app.renderer.height,
                worldWidth: Settings.worldSize,
                worldHeight: Settings.worldSize,
                events: View.app.renderer.events
            }));
        });
    }
}
